import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/data/categories';

export default function HeroSection() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(cat: typeof categories[0]) {
    setSelectedCategory(cat.name);
    setIsOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  }

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center overflow-hidden bg-gradient-to-br from-[#faf6f0] via-[#f8f0f4] to-[#f0eef8]">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0,0,0,0.15) 1px, transparent 1px), radial-gradient(circle at 80% 30%, rgba(0,0,0,0.1) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 pt-16 pb-16 md:pb-12">
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
          <div className="flex-1 w-full pt-8 md:pt-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-background-200/80 mb-8">
              <span className="w-2 h-2 rounded-full bg-primary-500" />
              <span className="text-xs md:text-sm font-medium text-foreground-700 tracking-wide uppercase">Superbuy Spreadsheet Tool</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-foreground-950 leading-[1.08] mb-6">
              Shop Across Borders.
              <br />
              <span className="italic font-normal text-foreground-700">Stay Organized.</span>
            </h1>

            <p className="text-base md:text-lg text-foreground-500 max-w-lg mb-0 leading-relaxed">
              The complete spreadsheet system to track, compare, and organize your cross-border shopping
              from China — all in one beautifully simple tool.
            </p>
          </div>

          <div className="flex-1 w-full flex flex-col justify-center pt-0 lg:pt-16">
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6 max-w-md">
              Browse our curated categories and discover thousands of products. Your ultimate shopping
              companion starts with a single click.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 bg-foreground-950 text-background-50 font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors duration-200 cursor-pointer"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line ml-2 text-base" />
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 border border-foreground-300 text-foreground-800 font-medium text-sm rounded-full hover:bg-foreground-50 transition-colors duration-200 cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <div ref={dropdownRef} className="relative w-full max-w-sm">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-5 py-3.5 bg-white/80 border border-background-200 rounded-xl text-sm text-foreground-700 hover:border-foreground-300 transition-colors duration-200 cursor-pointer"
              >
                <span>{selectedCategory || 'Browse Categories'}</span>
                {isOpen ? (
                  <i className="ri-arrow-up-s-line text-base transition-transform duration-200" />
                ) : (
                  <i className="ri-arrow-down-s-line text-base transition-transform duration-200" />
                )}
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-background-200 rounded-xl shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleSelect(cat)}
                      className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors duration-150 cursor-pointer flex items-center justify-between group"
                    >
                      <span>{cat.name}</span>
                      <i className="ri-external-link-line text-xs text-foreground-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[200px] md:h-[260px] overflow-hidden">
        <div className="w-full h-full rounded-t-[32px] md:rounded-t-[48px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20aerial%20view%20of%20fashion%20accessories%20hats%20bags%20and%20belts%20arranged%20in%20a%20flowing%20organic%20wave%20pattern%20on%20a%20warm%20sand%20colored%20textured%20surface%20with%20early%20morning%20golden%20sunlight%20casting%20long%20directional%20shadows%20from%20the%20bottom%20right%20creating%20a%20painterly%20editorial%20composition%20with%20rich%20amber%20and%20cream%20tones%20high%20end%20lifestyle%20product%20photography%20with%20negative%20space&width=1600&height=500&seq=hero-flatlay-v2&orientation=landscape"
            alt="Curated fashion products flat lay - Superbuy Spreadsheet shopping categories"
            title="Superbuy Spreadsheet Product Categories"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50/20 to-transparent pointer-events-none" />
        </div>
      </div>
    </section>
  );
}